import loadable from "react-loadable";
import { BrowserRouter, useHistory } from 'react-router-dom';
import { ToastProvider } from "@weezevent/nacre";
import { useStore } from "state";
import { i18n } from "i18n";
import { I18nextProvider , useTranslation } from "react-i18next";
import { Provider } from "react-redux";
import React from "react";
import { Loader } from "stateless/loader";
import { Provider as MixPanelProvider } from "mixpanel/index";
import SentryInitialize from "./sentry";
import { ExportController } from "exportController";
import Viewport from "nacre/viewport";
import { fetcher } from "@weezevent/weezjs-core";
import { Authentication } from "user/authentication";
import { useAuth } from 'react-oidc-context';

import moment from "moment";

const { mixpanel, connect, dsn } = window.weezConfig || {};

const App = loadable({
	loader: () => import("./target"),
	loading: Loader
});

i18n.init();

const LogRoutes = () => {
	/* istanbul ignore next */
	if (process.env.NODE_ENV === 'development') {
		// eslint-disable-next-line
		const history = useHistory();

		// eslint-disable-next-line
		React.useEffect(() => {
			return history.listen((location, action) => {
				console.info('RouteLog', action, 'location', location);
			});
		}, [history]);
	}
	return null;
};

const AuthToLocale = () => {
	const { user } = useAuth();
	const {i18n} = useTranslation();

	const userLocale = user.profile.locale || user.profile.data?.locale || 'en';

	React.useEffect(() => {
		if (userLocale) {
			const locale = userLocale.replace(/_/g, "-");

			if (locale !== i18n.language) {
				i18n.changeLanguage(locale);
				moment.locale(locale);
			}
		}
	// eslint-disable-next-line
	}, [userLocale]);
	return null;
};

const InnerApplication = () => {
	const store = useStore();

	// Set the API URL from the configuration provided by window.weezConfig.
	const apiUrl = window.weezConfig.api.url;

	// Initialize the fetcher module with the API URL.
	fetcher.initialize(apiUrl);

	// Use the useContext hook to access the token value from the TokenContext.
	const auth = useAuth();

	const accessToken = auth.user?.access_token;

	// Keep the fetcher token in sync with oidc-client-ts silent renewals.
	React.useEffect(() => {
		if (accessToken) {
			fetcher.setAuthorizationHeader(`Bearer ${accessToken}`);
		}
	}, [accessToken]);

	return <Provider store={ store }>
		<ToastProvider>
			{ dsn ?
				<SentryInitialize
					dsn={ dsn }
					release={ process.env_REACT_APP_RELEASE }
					environment={ window.location.host }
				/>
				: null
			}
			<AuthToLocale />
			<Viewport>
				<MixPanelProvider token={ mixpanel || '' } >
					<ExportController>
						<App />
					</ExportController>
				</MixPanelProvider>
			</Viewport>
		</ToastProvider>
	</Provider>;
};

export default function Application() {
	return <React.Suspense fallback={ Loader }>
		<BrowserRouter basename="/target">
			<Authentication redirectPath="/target/" config={ connect }>
				<LogRoutes />
				<I18nextProvider i18n={ i18n } >
					<InnerApplication />
				</I18nextProvider>
			</Authentication>
		</BrowserRouter>
	</React.Suspense>;
}
